import { Formik, FormikProps } from 'formik';
import React, { useCallback, useRef } from 'react';
import { Col, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as yup from 'yup';
import Button from '../../components/Button';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Typeahead from '../../components/Typeahead';
import { useCreatedTypeaheadOption } from '../../hooks/use-created-typeahead-option';
import { useOrganizationGroupSearch } from '../../hooks/use-organization-group-search';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  CREATE_ORG_GROUP_STORE_KEY,
  createOrganizationGroupRequested,
} from '../../redux/organization-group/organization-group.actions';
import {
  getCreatedOrganizationGroup,
  getCreateOrganizationGroupRequestId,
} from '../../redux/organization-group/organization-group.selectors';
import { getGroupPayloadValue, getGroupSelectValue, NO_ORGANIZATION_GROUP_VALUE } from '../../utils/organization-group.utils';

interface OrganizationGroupOption {
  id: string;
  name: string;
}

export interface OrganizationGroupEditorValue {
  organizationGroupId?: string | null;
  parentOrganizationGroupId?: string | null;
  organizationGroup?: OrganizationGroupOption | null;
  parentOrganizationGroup?: OrganizationGroupOption | null;
}

export interface OrganizationGroupChanges {
  organizationGroupId?: string | null;
  parentOrganizationGroupId?: string | null;
}

interface OrganizationGroupEditorProps {
  value: OrganizationGroupEditorValue;
  isUpdateLoading: boolean;
  onUpdate: (changes: OrganizationGroupChanges) => void;
}

interface OrganizationGroupFormValues {
  organizationGroupId: string;
  parentOrganizationGroupId: string;
}

const Field = styled.div`
  min-height: 86px;
`;

const ActionsContainer = styled.div`
  margin: 8px 0 0;
  text-align: right;
`;

const renderCreateOrganizationGroupOption = (name: string) => (
  <>
    <strong>+ Create New Parent:</strong> "{name}"
  </>
);

const ORGANIZATION_GROUP_VALIDATION_SCHEMA = yup.object().shape({
  organizationGroupId: yup.string().required('Required').notOneOf([NO_ORGANIZATION_GROUP_VALUE], 'Required'),
  parentOrganizationGroupId: yup.string().required('Required').notOneOf([NO_ORGANIZATION_GROUP_VALUE], 'Required'),
});

const OrganizationGroupEditor: React.FC<OrganizationGroupEditorProps> = ({ value, isUpdateLoading, onUpdate }) => {
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<OrganizationGroupFormValues> | null>(null);
  const createdOrganizationGroup = useSelector(getCreatedOrganizationGroup);
  const createRequestId = useSelector(getCreateOrganizationGroupRequestId);
  const {
    isLoading: isOrganizationGroupSearchLoading,
    organizationGroups,
    searchOrganizationGroups,
  } = useOrganizationGroupSearch({ searchOnMount: false });
  const {
    isLoading: isParentOrganizationGroupSearchLoading,
    organizationGroups: parentOrganizationGroups,
    searchOrganizationGroups: searchParentOrganizationGroups,
  } = useOrganizationGroupSearch({ searchOnMount: false });
  const isCreateLoading = useSelector(createLoadingSelector([CREATE_ORG_GROUP_STORE_KEY]));
  const createOrganizationGroup = useCallback(
    (name: string, requestId: string) => {
      dispatch(
        createOrganizationGroupRequested({
          name,
          requestId,
        }),
      );
    },
    [dispatch],
  );
  const typeaheadCreate = useCreatedTypeaheadOption<
    OrganizationGroupFormValues,
    keyof OrganizationGroupFormValues,
    OrganizationGroupOption
  >({
    formikRef,
    createdOption: createdOrganizationGroup,
    createRequestId,
    emptyValue: NO_ORGANIZATION_GROUP_VALUE,
    onCreateOption: createOrganizationGroup,
  });
  const initialFormValues: OrganizationGroupFormValues = {
    organizationGroupId: getGroupSelectValue(value.organizationGroupId),
    parentOrganizationGroupId: getGroupSelectValue(value.parentOrganizationGroupId),
  };

  const onSubmit = (values: OrganizationGroupFormValues) => {
    const changes: OrganizationGroupChanges = {};

    if (values.organizationGroupId !== initialFormValues.organizationGroupId) {
      changes.organizationGroupId = getGroupPayloadValue(values.organizationGroupId);
    }
    if (values.parentOrganizationGroupId !== initialFormValues.parentOrganizationGroupId) {
      changes.parentOrganizationGroupId = getGroupPayloadValue(values.parentOrganizationGroupId);
    }

    onUpdate(changes);
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formikRef}
      initialValues={initialFormValues}
      onSubmit={onSubmit}
      validateOnMount
      validationSchema={ORGANIZATION_GROUP_VALIDATION_SCHEMA}
    >
      {({ values, dirty, errors, handleSubmit, isValid, touched }) => (
        <form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Field>
                <FieldWithLabel label="Parent *" touched={touched.organizationGroupId} error={errors.organizationGroupId}>
                  <Typeahead
                    emptyValue={NO_ORGANIZATION_GROUP_VALUE}
                    name="organizationGroupId"
                    options={organizationGroups}
                    selectedOption={typeaheadCreate.getSelectedOption(
                      'organizationGroupId',
                      values.organizationGroupId,
                      value.organizationGroup,
                    )}
                    placeholder="Not set"
                    value={values.organizationGroupId}
                    disabled={isCreateLoading}
                    isLoading={isCreateLoading || isOrganizationGroupSearchLoading}
                    onChange={typeaheadCreate.onChange('organizationGroupId')}
                    onSearch={searchOrganizationGroups}
                    onCreateOption={typeaheadCreate.onCreateOption('organizationGroupId')}
                    renderCreateOption={renderCreateOrganizationGroupOption}
                  />
                </FieldWithLabel>
              </Field>
            </Col>
            <Col md={6}>
              <Field>
                <FieldWithLabel
                  label="Ultimate parent *"
                  touched={touched.parentOrganizationGroupId}
                  error={errors.parentOrganizationGroupId}
                >
                  <Typeahead
                    emptyValue={NO_ORGANIZATION_GROUP_VALUE}
                    name="parentOrganizationGroupId"
                    options={parentOrganizationGroups}
                    selectedOption={typeaheadCreate.getSelectedOption(
                      'parentOrganizationGroupId',
                      values.parentOrganizationGroupId,
                      value.parentOrganizationGroup,
                    )}
                    placeholder="Not set"
                    value={values.parentOrganizationGroupId}
                    disabled={isCreateLoading}
                    isLoading={isCreateLoading || isParentOrganizationGroupSearchLoading}
                    onChange={typeaheadCreate.onChange('parentOrganizationGroupId')}
                    onSearch={searchParentOrganizationGroups}
                    onCreateOption={typeaheadCreate.onCreateOption('parentOrganizationGroupId')}
                    renderCreateOption={renderCreateOrganizationGroupOption}
                  />
                </FieldWithLabel>
              </Field>
            </Col>
          </Row>
          <ActionsContainer>
            <Button type="submit" disabled={!dirty || !isValid || isUpdateLoading || isCreateLoading}>
              Update Parent/Ultimate Parent
            </Button>
          </ActionsContainer>
        </form>
      )}
    </Formik>
  );
};

export default OrganizationGroupEditor;
